import InfoBlock from './InfoBlock'
import React, { useState } from 'react'
import { useCombobox } from 'downshift'
import { Option } from '@mortgage-pos/types'
import maxSize from 'popper-max-size-modifier'
import Caret from '@mortgage-pos/ui/assets/Caret.svg'
import { Modifier, usePopper } from 'react-popper'
import CloseIcon from '@mortgage-pos/ui/assets/black-x.svg'

export interface Props {
  name: string
  title: string
  subTitle: string
  value: string
  error?: string
  touched: boolean
  disabled?: boolean
  modifierClass?: string
  options: Option[]
  setValue: (value: string) => void
}

const Select = ({
  name,
  title,
  subTitle,
  value,
  error,
  touched,
  disabled = false,
  modifierClass,
  infoContent,
  options,
  setValue,
}) => {
  const [inputItems, setInputItems] = useState(options)
  const [anchorElement, setAnchorElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)

  const { styles, attributes, update } = usePopper(
    anchorElement,
    popperElement,
    {
      modifiers,
    }
  )

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    selectItem,
    reset,
  } = useCombobox({
    items: inputItems,

    itemToString,

    onIsOpenChange: () => {
      update()
    },

    initialInputValue: options.find((opt) => opt.value === value)?.text || '',

    initialSelectedItem: options.find((opt) => opt.value === value) || null,

    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setValue(selectedItem.value)
        return
      }
      setValue(null)
    },

    onInputValueChange: ({ inputValue }) => {
      setInputItems(determineOptions({ options, inputValue }))

      // handles state selects where states can be entered via abbreviation or full length
      const autofillItem = options.find(
        (opt) =>
          opt.value.toLowerCase() === inputValue.toLowerCase() ||
          opt.text.toLowerCase() === inputValue.toLowerCase()
      )
      if (autofillItem) {
        setValue(autofillItem.value)
      }
    },
  })

  const { onClick: handleClick, ...toggleButtonProps } = getToggleButtonProps({
    className: `select__toggle-button ${modifierClass ? modifierClass : ''}`,
    disabled,
  })

  return (
    <div
      key={`select-${name}`}
      className={`select ${modifierClass ? modifierClass : ''}`}
    >
      {title ? <div className="select__title">{title}</div> : null}
      <div
        {...getComboboxProps({
          className: `input ${error && touched ? '--error' : ''}
          ${modifierClass ? modifierClass : ''}
          `,
          disabled,
        })}
      >
        {subTitle ? (
          <label
            htmlFor={name}
            {...getLabelProps({
              id: `select-label-${name}`,
              className: `select__subtitle ${isOpen ? '--active' : ''} ${
                error && touched ? `--error` : ''
              }
              ${modifierClass ? modifierClass : ''}
              `,
              disabled,
            })}
          >
            {subTitle}
          </label>
        ) : null}
        {!infoContent || (touched && error) ? null : (
          <InfoBlock>{infoContent}</InfoBlock>
        )}
        <div className="select__input-wrapper">
          {selectedItem && inputValue ? (
            <button
              type="button"
              {...toggleButtonProps}
              onClick={() => {
                selectItem(null)
                reset()
              }}
              aria-label="toggle menu"
            >
              <img src={CloseIcon} alt="Clear" className={`select__caret`} />
            </button>
          ) : (
            <button
              type="button"
              {...toggleButtonProps}
              onClick={handleClick}
              aria-label="toggle menu"
            >
              <img
                src={Caret}
                alt="Select Caret"
                className={`select__caret ${isOpen ? '--active' : ''}`}
              />
            </button>
          )}
          <input
            {...getInputProps({
              className: `select__anchor ${isOpen ? '--active' : ''}`,
              ref: setAnchorElement,
              disabled,
            })}
            onClick={handleClick}
            data-testid={`select-anchor-${name}`}
            name={name}
            aria-label={name}
          />
        </div>

        <ul
          title="select-body"
          ref={popperElement}
          id="select-body"
          data-testid="select-body"
          {...getMenuProps({
            ref: setPopperElement,
            className: `select__popper ${isOpen ? '--open' : ''}`,
            disabled,
          })}
          {...attributes.popper}
          style={styles.popper}
        >
          {inputItems.length > 0 ? (
            inputItems.map((item, index) => (
              <li
                key={`${item.value}${index}`}
                data-testid={`${item.value}${index}`}
                {...getItemProps({
                  item,
                  index,
                  className: `select__option ${
                    highlightedIndex === index ? '--active' : ''
                  }`,
                  disabled,
                })}
              >
                {item.text}
              </li>
            ))
          ) : (
            <li className="select__option --none">No Options</li>
          )}
        </ul>
      </div>
      {error && touched ? <InfoBlock type="danger">{error}</InfoBlock> : null}
    </div>
  )
}

export default Select

const modifiers: Modifier<string, object>[] = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['top', 'right'],
    },
  },
  {
    name: 'offset',
    options: {
      offset: [0, 10],
    },
  },
  // custom modifier to set the popper element to the same width as the anchor element
  {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.width = `${state.rects.reference.width}px`
    },
    // @ts-ignore
    effect: ({ state }) => {
      // @ts-ignore. Popper jst typings are incomplete for html elements
      state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`
    },
  },
  maxSize,
  {
    name: 'applyMaxSize',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['maxSize'],
    fn({ state }) {
      const { height } = state.modifiersData.maxSize
      state.styles.popper.maxHeight = `${height / 1.2}px`
    },
  },
]

const itemToString = (option) => (option ? option.text : '')

const determineOptions = ({ options, inputValue }) =>
  options.filter((opt) =>
    itemToString(opt).toLowerCase().startsWith(inputValue.toLowerCase())
  )
